import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { useToast } from '../ui/use-toast';
import { MapleGameHost } from '@maple/engine-react';
import type { MapleEngine } from '@maple/engine-core';
import { Orbit, Maximize2, Minimize2, Sparkles, Telescope, Play, Pause, Eye, EyeOff } from 'lucide-react';

// Three.js imports
import {
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
  Color,
  AmbientLight,
  DirectionalLight,
  Mesh,
  MeshStandardMaterial,
  MeshBasicMaterial,
  SphereGeometry,
  TextureLoader,
  Vector3,
  PointsMaterial,
  BufferGeometry,
  Points,
  Float32BufferAttribute,
  BackSide,
  RingGeometry,
  DoubleSide,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Line, LineBasicMaterial } from 'three';

type PlanetDef = {
  name: string;
  radius: number;       // visual units
  orbitRadius: number;  // visual units
  periodDays: number;   // orbital period
  color?: number;
  textureUrl?: string;
  ring?: {
    innerMul: number;        // inner radius multiplier of planet radius
    outerMul: number;        // outer radius multiplier of planet radius
    tilt?: number;           // degrees
    textureUrl?: string;     // ring texture with alpha
    opacity?: number;        // 0..1
  };
};

const PLANETS: PlanetDef[] = [
  { name: 'Mercury', radius: 0.4, orbitRadius: 6,  periodDays: 88,  color: 0xb1b3b5 },
  { name: 'Venus',   radius: 0.95, orbitRadius: 8,  periodDays: 225, color: 0xd3b56a },
  { name: 'Earth',   radius: 1.0,  orbitRadius: 10, periodDays: 365, color: 0x2c7fb8, textureUrl: 'https://upload.wikimedia.org/wikipedia/commons/7/7f/Earth_Western_Hemisphere.jpg' },
  { name: 'Mars',    radius: 0.53, orbitRadius: 12, periodDays: 687, color: 0xb5533d },
  { name: 'Jupiter', radius: 11,   orbitRadius: 16, periodDays: 4333, color: 0xd9c7a6, textureUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/5a/Jupiter_Oct_2022.jpg' },
  { 
    name: 'Saturn',  radius: 9,    orbitRadius: 20, periodDays: 10759, color: 0xdbc7a1,
    ring: {
      innerMul: 1.3,        // relative to planet radius
      outerMul: 2.5,
      tilt: 26.7,
      textureUrl: 'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/planets/saturnring.png',
      opacity: 0.9
    }
  },
  { 
    name: 'Uranus',  radius: 4,    orbitRadius: 24, periodDays: 30687, color: 0x7fc2c7,
    ring: {
      innerMul: 1.2,
      outerMul: 1.6,
      tilt: 97.8,
      textureUrl: 'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/planets/uranusring.png',
      opacity: 0.7
    }
  },
  { name: 'Neptune', radius: 3.9,  orbitRadius: 28, periodDays: 60190, color: 0x4062a7 }
];

const SUN_RADIUS = 3.0;

// NASA APOD endpoint (uses DEMO_KEY by default)
const APOD_API_URL = 'https://api.nasa.gov/planetary/apod?api_key=DEMO_KEY';

const MapleStar: React.FC = () => {
  const { toast } = useToast();

  // UI state
  const rootRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hideMenus, setHideMenus] = useState<boolean>(false);

  const [selectedPlanet, setSelectedPlanet] = useState<string>('Earth');
  const [followPlanet, setFollowPlanet] = useState<boolean>(true);
  const [showOrbits, setShowOrbits] = useState<boolean>(true);
  const [showLabels, setShowLabels] = useState<boolean>(true);
  const [timeWarp, setTimeWarp] = useState<number>(5); // simulation speed multiplier
  // New: scale preset for readability
  const [scalePreset, setScalePreset] = useState<'balanced' | 'realistic' | 'compact' | 'mini'>('balanced');

  const [useApodSkybox, setUseApodSkybox] = useState<boolean>(false);
  const [apodUrl, setApodUrl] = useState<string>('');
  const [query, setQuery] = useState<string>(''); // future use: goto star system by name

  // HUD
  const [fps, setFps] = useState<number>(0);
  const [camInfo, setCamInfo] = useState<{ x: number; y: number; z: number }>({ x: 0, y: 0, z: 0 });

  const targetFPS = 60;

  // Refs for live game parameters
  const followPlanetRef = useRef<boolean>(followPlanet);
  const selectedPlanetRef = useRef<string>(selectedPlanet);
  const showOrbitsRef = useRef<boolean>(showOrbits);
  const showLabelsRef = useRef<boolean>(showLabels);
  const timeWarpRef = useRef<number>(timeWarp);
  const useApodSkyboxRef = useRef<boolean>(useApodSkybox);
  const apodUrlRef = useRef<string>(apodUrl);
  // New: live scaling refs
  const radiusScaleRef = useRef<number>(0.35);  // base planet & sun visual size scale
  const orbitScaleRef = useRef<number>(1.8);    // orbit spacing scale
  const gammaRef = useRef<number>(0.7);         // non-linear size compression (1 = linear)
  // Keep references to created objects for rescaling on the fly
  const planetsRef = useRef<Record<string, { mesh: Mesh; orbit: Line | null; ring: Mesh | null }>>({});
  const sunRef = useRef<Mesh | null>(null);

  useEffect(() => { followPlanetRef.current = followPlanet; }, [followPlanet]);
  useEffect(() => { selectedPlanetRef.current = selectedPlanet; }, [selectedPlanet]);
  useEffect(() => { showOrbitsRef.current = showOrbits; }, [showOrbits]);
  useEffect(() => { showLabelsRef.current = showLabels; }, [showLabels]);
  useEffect(() => { timeWarpRef.current = timeWarp; }, [timeWarp]);
  useEffect(() => { useApodSkyboxRef.current = useApodSkybox; }, [useApodSkybox]);
  useEffect(() => { apodUrlRef.current = apodUrl; }, [apodUrl]);

  // New: apply preset changes to scaling
  useEffect(() => {
    const presets = {
      balanced:  { radius: 0.35, orbit: 1.9, gamma: 0.7 },
      realistic: { radius: 1.00, orbit: 1.0, gamma: 1.0 },
      compact:   { radius: 0.22, orbit: 2.2, gamma: 0.6 },
      mini:      { radius: 0.12, orbit: 2.8, gamma: 0.5 },
    } as const;
    const { radius, orbit, gamma } = presets[scalePreset];
    radiusScaleRef.current = radius;
    orbitScaleRef.current = orbit;
    gammaRef.current = gamma;
    // Rescale existing objects immediately
    if (sunRef.current) {
      sunRef.current.scale.set(radius, radius, radius);
    }
    const map = planetsRef.current;
    Object.keys(map).forEach((name) => {
      const p = map[name];
      const def = PLANETS.find(d => d.name === name);
      if (!p || !def) return;
      const s = radius * Math.pow(def.radius, gamma - 1); // non-linear compression
      p.mesh.scale.set(s, s, s);
      if (p.orbit) p.orbit.scale.set(orbit, 1, orbit); // scale orbit ring in XZ
    });
  }, [scalePreset]);

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
      toast({ title: 'Entering full screen' });
    } else {
      document.exitFullscreen?.();
      toast({ title: 'Exiting full screen' });
    }
  }, [toast]);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Fetch NASA APOD for optional skybox
  const fetchApod = useCallback(async () => {
    try {
      const resp = await fetch(APOD_API_URL);
      if (!resp.ok) throw new Error('NASA APOD request failed');
      const json = await resp.json();
      const url = json.hdurl || json.url;
      if (!url || typeof url !== 'string') {
        toast({ title: 'APOD unavailable', description: 'No image for today.', });
        return;
      }
      setApodUrl(url);
      setUseApodSkybox(true);
      toast({ title: 'APOD fetched', description: 'Skybox set to NASA image of the day.' });
    } catch (err: any) {
      toast({ title: 'NASA fetch error', description: err?.message || 'Could not load APOD.' });
    }
  }, [toast]);

  // Create the MapleEngine game
  const createGame = useCallback((engine: MapleEngine) => {
    // FPS measurement
    let frameCount = 0;
    let last = performance.now();
    const statsSystem = {
      update() {
        frameCount++;
        const now = performance.now();
        if (now - last >= 500) {
          const measuredFps = Math.round((frameCount * 1000) / (now - last));
          setFps(measuredFps);
          frameCount = 0;
          last = now;
        }
      },
    };

    const canvas = engine.getCanvas();
    canvas.width = canvas.width;
    canvas.height = canvas.height;

    const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.max(1, window.devicePixelRatio || 1));
    renderer.setSize(canvas.width, canvas.height, false);

    const scene = new Scene();
    scene.background = new Color(0x0b1220);

    const camera = new PerspectiveCamera(60, canvas.width / canvas.height, 0.1, 2000);
    camera.position.set(0, 10, 36);

    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.target.set(0, 0, 0);

    // Lights
    const ambient = new AmbientLight(0xffffff, 0.35);
    const dir = new DirectionalLight(0xffffff, 1.0);
    dir.position.set(8, 4, 2);
    scene.add(ambient, dir);

    // Stars backdrop
    const addStars = (count = 3000, spread = 600) => {
      const geom = new BufferGeometry();
      const positions = new Float32BufferAttribute(count * 3, 3);
      for (let i = 0; i < count; i++) {
        positions.setXYZ(
          i,
          (Math.random() - 0.5) * spread,
          (Math.random() - 0.5) * spread,
          (Math.random() - 0.5) * spread
        );
      }
      geom.setAttribute('position', positions);
      const mat = new PointsMaterial({ color: 0xffffff, size: 0.8 });
      const points = new Points(geom, mat);
      scene.add(points);
    };
    addStars();

    // Skybox dome (for APOD image)
    let skyDome: Mesh | null = null;
    const loader = new TextureLoader();
    const applySkybox = (url: string) => {
      if (!url) return;
      try {
        loader.load(url, (tex) => {
          if (skyDome) {
            scene.remove(skyDome);
            skyDome.geometry.dispose();
            (skyDome.material as MeshBasicMaterial).map?.dispose?.();
            (skyDome.material as MeshBasicMaterial).dispose();
            skyDome = null;
          }
          skyDome = new Mesh(
            new SphereGeometry(800, 64, 64),
            new MeshBasicMaterial({ map: tex, side: BackSide })
          );
          skyDome.rotation.order = 'YXZ';
          scene.add(skyDome);
        });
      } catch {
        // ignore texture failures
      }
    };
    if (useApodSkyboxRef.current && apodUrlRef.current) {
      applySkybox(apodUrlRef.current);
    }

    // Sun
    const sun = new Mesh(
      new SphereGeometry(SUN_RADIUS, 64, 64),
      new MeshStandardMaterial({ emissive: 0xffcc66, emissiveIntensity: 0.8, color: 0x553311 })
    );
    sun.position.set(0, 0, 0);
    scene.add(sun);
    // Apply current scale to sun
    sun.scale.set(radiusScaleRef.current, radiusScaleRef.current, radiusScaleRef.current);
    sunRef.current = sun;

    // Planets & orbits
    const planets: Record<string, { mesh: Mesh; orbit: (Line | null); ring: Mesh | null }> = {};

    // Helper: make a thin orbit ring in XZ plane
    const makeOrbitRing = (radius: number, segments = 256) => {
      const positions = new Float32BufferAttribute((segments + 1) * 3, 3);
      for (let i = 0; i <= segments; i++) {
        const t = (i / segments) * Math.PI * 2;
        const x = Math.cos(t) * radius;
        const z = Math.sin(t) * radius;
        positions.setXYZ(i, x, 0, z); // y=0, XZ plane
      }
      const geom = new BufferGeometry();
      geom.setAttribute('position', positions);
      const mat = new LineBasicMaterial({
        color: 0x445566,
        transparent: true,
        opacity: 0.35,
        depthWrite: false, // don't muddy depth buffer
      });
      const line = new Line(geom, mat);
      line.name = `orbit-${radius}`;
      return line;
    };

    for (const def of PLANETS) {
      const mat = new MeshStandardMaterial({ color: def.color ?? 0xffffff, roughness: 0.7, metalness: 0.0 });
      if (def.textureUrl) {
        try {
          loader.load(def.textureUrl, (tex) => {
            mat.map = tex;
            mat.needsUpdate = true;
          });
        } catch {
          // fallback color material
        }
      }
      const mesh = new Mesh(new SphereGeometry(def.radius, 64, 64), mat);
      mesh.position.set(def.orbitRadius, 0, 0);
      scene.add(mesh);
      // Apply current size scale (non-linear)
      {
        const s = radiusScaleRef.current * Math.pow(def.radius, gammaRef.current - 1);
        mesh.scale.set(s, s, s);
      }

      // Optional: Rings for ringed planets (child of planet so they scale/tilt with it)
      let ringMesh: Mesh | null = null;
      if (def.ring) {
        const { innerMul, outerMul, tilt = 0, textureUrl, opacity = 0.85 } = def.ring;
        const ringGeom = new RingGeometry(def.radius * innerMul, def.radius * outerMul, 128);
        const ringMat = new MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity,
          side: DoubleSide,
          depthWrite: false,
        });
        if (textureUrl) {
          try {
            loader.load(textureUrl, (tex) => {
              ringMat.map = tex;
              ringMat.needsUpdate = true;
            });
          } catch {
            // keep flat translucent ring if texture fails
          }
        }
        ringMesh = new Mesh(ringGeom, ringMat);
        // RingGeometry lies in XY plane; rotate to planet's equatorial plane (XZ) then apply axial tilt
        ringMesh.rotation.x = Math.PI / 2;
        ringMesh.rotation.z = (tilt * Math.PI) / 180;
        // Attach to planet so it follows position and scale
        mesh.add(ringMesh);
      }

      // Orbit ring as a 2D line circle
      const orbit = makeOrbitRing(def.orbitRadius);
      scene.add(orbit);
      // Scale orbit spacing (XZ only)
      orbit.scale.set(orbitScaleRef.current, 1, orbitScaleRef.current);

      planets[def.name] = { mesh, orbit, ring: ringMesh };
    }
    // Expose for on-the-fly rescaling via refs
    planetsRef.current = planets;

    // Simulation clock
    let simTime = 0; // days
    const TWO_PI = Math.PI * 2;

    // Main update: planet positions and camera follow
    const simSystem = {
      update(dt: number) {
        simTime += dt * 60 * timeWarpRef.current; // scale seconds to "days" loosely

        // update planet positions
        for (const def of PLANETS) {
          const p = planets[def.name];
          if (!p) continue;
          const ang = TWO_PI * (simTime / def.periodDays);
          const x = (def.orbitRadius * orbitScaleRef.current) * Math.cos(ang);
          const z = (def.orbitRadius * orbitScaleRef.current) * Math.sin(ang);
          p.mesh.position.set(x, 0, z);

          // show/hide orbit ring
          if (p.orbit) p.orbit.visible = showOrbitsRef.current;
        }

        // Camera follow
        if (followPlanetRef.current) {
          const target = planets[selectedPlanetRef.current]?.mesh;
          if (target) {
            const tp = target.position;
            // smooth follow
            controls.target.lerp(tp, 0.15);
            // Slightly adapt camera follow distance to orbit scale
            const back = new Vector3(-8 * radiusScaleRef.current, 4 * radiusScaleRef.current, 12 * radiusScaleRef.current);
            const desired = new Vector3().copy(tp).add(back);
            camera.position.lerp(desired, 0.08);
          }
        }

        // Update skybox if APOD toggled
        if (useApodSkyboxRef.current && apodUrlRef.current) {
          if (!skyDome) applySkybox(apodUrlRef.current);
        } else {
          if (skyDome) {
            scene.remove(skyDome);
            skyDome.geometry.dispose();
            (skyDome.material as MeshBasicMaterial).map?.dispose?.();
            (skyDome.material as MeshBasicMaterial).dispose();
            skyDome = null;
          }
        }

        // update camera info HUD
        setCamInfo({ x: camera.position.x, y: camera.position.y, z: camera.position.z });

        controls.update();
      },
    };

    // Resize handling
    let prevW = canvas.width, prevH = canvas.height;
    const resizeSystem = {
      update() {
        if (canvas.width !== prevW || canvas.height !== prevH) {
          renderer.setSize(canvas.width, canvas.height, false);
          camera.aspect = canvas.width / canvas.height;
          camera.updateProjectionMatrix();
          prevW = canvas.width; prevH = canvas.height;
        }
      },
    };

    const renderSystem = {
      update() {
        renderer.render(scene, camera);
      },
    };

    // Add systems
    engine.addSystem(statsSystem);
    engine.addSystem(simSystem);
    engine.addSystem(resizeSystem);
    engine.addSystem(renderSystem);
  }, []);

  // Small helpers
  const planetList = useMemo(() => PLANETS.map(p => p.name), []);
  const selectedDef = useMemo(() => PLANETS.find(p => p.name === selectedPlanet), [selectedPlanet]);

  return (
    <div ref={rootRef} className="h-full w-full relative bg-black">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-muted/30 border-b px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Orbit className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-medium">MapleStar</h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">v1</Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setHideMenus((v) => !v)}
            title={hideMenus ? 'Show menus' : 'Hide menus'}
          >
            {hideMenus ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={toggleFullscreen} title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}>
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Control panel (left) */}
      {!hideMenus && (
      <div className="absolute top-3 left-3 z-10 w-[320px]">
        <Card className="bg-black/60 border-white/10 text-white p-3 backdrop-blur-sm space-y-3">
          <div className="flex items-center gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search system (future)"
              className="bg-white/10 text-white placeholder:text-white/60"
            />
            <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white">
              Go
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm">Scale preset</span>
            <Select value={scalePreset} onValueChange={(v: any) => setScalePreset(v)}>
              <SelectTrigger className="w-[180px] bg-white/10 text-white border-white/20">
                <SelectValue placeholder="Balanced" />
              </SelectTrigger>
              <SelectContent className="bg-neutral-900 text-white">
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="realistic">Realistic-ish</SelectItem>
                <SelectItem value="compact">Compact</SelectItem>
                <SelectItem value="mini">Mini</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm">Planet</span>
            <Select value={selectedPlanet} onValueChange={(v) => setSelectedPlanet(v)}>
              <SelectTrigger className="w-[180px] bg-white/10 text-white border-white/20">
                <SelectValue placeholder="Choose a planet" />
              </SelectTrigger>
              <SelectContent className="bg-neutral-900 text-white">
                {planetList.map((name) => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm">Follow selected</span>
            <Switch checked={followPlanet} onCheckedChange={setFollowPlanet} />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm">Show orbits</span>
            <Switch checked={showOrbits} onCheckedChange={setShowOrbits} />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm">Labels</span>
            <Switch checked={showLabels} onCheckedChange={setShowLabels} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Time warp</span>
              <span className="font-mono">{timeWarp.toFixed(1)}×</span>
            </div>
            <Slider
              value={[timeWarp]}
              max={50}
              min={0.1}
              step={0.1}
              onValueChange={(v) => setTimeWarp(v[0] ?? 1)}
            />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm">NASA APOD skybox</span>
            <Switch checked={useApodSkybox} onCheckedChange={setUseApodSkybox} />
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={fetchApod}
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white"
              title="Use NASA APOD as skybox"
            >
              <Sparkles className="h-4 w-4" />
              Fetch APOD
            </Button>
            <Button
              onClick={() => {
                setUseApodSkybox(false);
                setApodUrl('');
                toast({ title: 'Skybox cleared' });
              }}
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white"
            >
              Clear
            </Button>
          </div>
        </Card>
      </div>
      )}

      {/* Info panel (right) */}
      {!hideMenus && (
      <div className="absolute top-3 right-3 z-10 w-[360px]">
        <Card className="bg-black/60 border-white/10 text-white p-3 backdrop-blur-sm space-y-3">
          <div className="flex items-center gap-2">
            <Telescope className="h-4 w-4" />
            <span className="text-sm font-medium">Explorer</span>
          </div>
          <div className="text-xs text-white/80 space-y-1">
            <div>FPS: <span className="font-mono">{fps}</span></div>
            <div>Camera: <span className="font-mono">{camInfo.x.toFixed(1)}, {camInfo.y.toFixed(1)}, {camInfo.z.toFixed(1)}</span></div>
          </div>
          {selectedDef && (
            <div className="text-xs text-white/80 space-y-1">
              <div>Selected: <span className="font-semibold">{selectedDef.name}</span></div>
              <div>Radius (viz): <span className="font-mono">{selectedDef.radius}</span></div>
              <div>Orbit radius: <span className="font-mono">{selectedDef.orbitRadius}</span></div>
              <div>Period (days): <span className="font-mono">{selectedDef.periodDays}</span></div>
              <div className="opacity-70">Note: orbital motion is simplified for smooth exploration.</div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/10 hover:bg-white/20 text-white"
              onClick={() => setSelectedPlanet('Earth')}
            >
              Follow Earth
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/10 hover:bg-white/20 text-white"
              onClick={() => setSelectedPlanet('Jupiter')}
            >
              Visit Jupiter
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/10 hover:bg-white/20 text-white"
              onClick={() => setSelectedPlanet('Neptune')}
            >
              Edge of system
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/10 hover:bg-white/20 text-white"
              onClick={() => setFollowPlanet(true)}
              title="Enable follow"
            >
              <Play className="h-4 w-4" />
              Follow
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/10 hover:bg-white/20 text-white"
              onClick={() => setFollowPlanet(false)}
              title="Disable follow"
            >
              <Pause className="h-4 w-4" />
              Free cam
            </Button>
          </div>
        </Card>
      </div>
      )}

      {/* Preview viewport: MapleEngine host */}
      <div className="absolute inset-0">
        <MapleGameHost
          appId="maple-star"
          targetFPS={targetFPS}
          createGame={createGame}
          className="h-full w-full"
          renderMode="webgl"
        />
      </div>
    </div>
  );
};

export default MapleStar;